import { RenderResult } from '@dojo/framework/core/interfaces';
import { createICacheMiddleware } from '@dojo/framework/core/middleware/icache';
import { theme } from '@dojo/framework/core/middleware/theme';
// Dojo
import { create, v, w } from '@dojo/framework/core/vdom';
import { formatAriaProperties } from '@dojo/widgets/common/util';
import { Tooltip } from '@dojo/widgets/tooltip';
import { Icon } from '@holmescorp/base-widgets';
import { convertToBoolean } from '@holmescorp/common';
// Holmescorp
import { createNlsMiddleware } from '@holmescorp/lcc-state';
import { publish } from '@holmescorp/pub-sub';

// CSS
import css from '../css/card.m.css';
import { CardProperties } from '../interfaces';

import { Keys } from '@holmescorp/common';

export interface CardChildren {
	header?: RenderResult;
	content?: RenderResult;
}

export interface CardICache {
	promote_ttopen: boolean;
	demote_ttopen: boolean;
}

const icache = createICacheMiddleware<CardICache>();

const factory = create({ icache, theme, nls: createNlsMiddleware('ditabook') })
	.properties<CardProperties>()
	.children<CardChildren | undefined>();

export const Card = factory(({ id, children, properties, middleware: { icache, theme, nls } }) => {
	const widgetId = `card-${id}`;

	const themeCss = theme.classes(css);

	const {
		xqa,
		aria = {},
		mediaSrc,
		mediaTitle,
		disabled = false,
		nlsKey = '',
		ispromotedisabled = 'true',
		ispromotable = 'false',
		ispromoted = 'false'
	} = properties();

	const { header, content } = children()[0] || ({} as CardChildren);

	const {
		messages: { card_promote_tooltip = '', card_demote_tooltip = '' }
	} = nls();
	// convert incoming properties into true booleans
	const props = {
		ispromotedisabled: convertToBoolean(ispromotedisabled),
		ispromotable: convertToBoolean(ispromotable),
		ispromoted: convertToBoolean(ispromoted)
	};

	const promote_actions = v(
		'div',
		{
			classes: [themeCss.promote, !props.ispromotable ? themeCss.disabledPromote : null, 'cardActions'],
			role: 'button',
			tabIndex: 0,
			...formatAriaProperties({
				label: props.ispromoted ? card_demote_tooltip : card_promote_tooltip
			}),
			onmouseover: () =>
				props.ispromotable && icache.set(`${props.ispromoted ? 'demote' : 'promote'}_ttopen`, true),
			onmouseout: () =>
				props.ispromotable && icache.set(`${props.ispromoted ? 'demote' : 'promote'}_ttopen`, false),
			onfocus: () => props.ispromotable && icache.set(`${props.ispromoted ? 'demote' : 'promote'}_ttopen`, true),
			onblur: () => props.ispromotable && icache.set(`${props.ispromoted ? 'demote' : 'promote'}_ttopen`, false),
			onclick: () => {
				if (props.ispromotable) {
					publish('tool:studyplan:modify', {
						key: nlsKey,
						type: 'resource',
						state: props.ispromoted ? 'promoted' : 'demoted',
						action: props.ispromoted ? 'remove' : 'add'
					});
				}
				icache.set('promote_ttopen', false);
				icache.set('demote_ttopen', false);
			},
			onkeydown: (event: KeyboardEvent) => {
				switch (event.key) {
					case Keys.Enter:
					case Keys.Space:
					case Keys.Spacebar:
						event.preventDefault();
						if (props.ispromotable) {
							publish('tool:studyplan:modify', {
								key: nlsKey,
								type: 'resource',
								state: props.ispromoted ? 'promoted' : 'demoted',
								action: props.ispromoted ? 'remove' : 'add'
							});
						}
						icache.set('promote_ttopen', false);
						icache.set('demote_ttopen', false);
						break;
					case Keys.Escape:
						icache.set('promote_ttopen', false);
						icache.set('demote_ttopen', false);
						break;
				}
			}
		},
		[
			props.ispromoted
				? w(
						Tooltip,
						{
							open: icache.getOrSet('demote_ttopen', false),
							orientation: 'top'
						},
						[
							{
								content: card_demote_tooltip,
								trigger: v('span', { 'aria-hidden': 'true' }, [
									w(Icon, { fapre: 'far', faname: 'square-minus' })
								])
							}
						]
				  )
				: w(
						Tooltip,
						{
							open: icache.getOrSet('promote_ttopen', false),
							orientation: 'top'
						},
						[
							{
								content: card_promote_tooltip,
								trigger: v('span', { 'aria-hidden': 'true' }, [
									w(Icon, { fapre: 'far', faname: 'square-plus' })
								])
							}
						]
				  )
		]
	);
	// onclick: (ev: MouseEvent) => {
	// 	ev.stopPropagation();
	// 	expandable && icache.set('expanded', true);
	// },
	// onkeydown: (ev: KeyboardEvent) => {
	// 	switch (ev.key) {
	// 		case Keys.Enter:
	// 		case Keys.Space:
	// 		case Keys.Spacebar:
	// 			ev.preventDefault();
	// 			expandable && icache.set('expanded', true);
	// 	}
	// }

	return v(
		'div',
		{
			id,
			widgetId,
			xqa,
			...formatAriaProperties(aria),
			classes: [themeCss.root, theme.variant(), disabled ? themeCss.disabled : null],
			role: 'group',
			tabIndex: 0
		},
		[
			v(
				'div',
				{
					key: 'content',
					classes: [themeCss.content]
				},
				[
					header ? v('h2', { key: 'header', classes: [themeCss.header] }, [header as any]) : null,
					mediaSrc
						? v('img', {
								key: 'media',
								title: mediaTitle,
								alt: mediaTitle,
								classes: [themeCss.media],
								src: `${mediaSrc}`
						  })
						: null,
					content
						? v('div', {}, [
								props.ispromoted === undefined || props.ispromotedisabled ? null : promote_actions,
								content as any
						  ])
						: null
				]
			)
		]
	);
});

export default Card;
